/**
 * @author Daniel Aguirre
 * @file saga.js
 * @description Patient Saga
 */

// Dependencies
import { put, call, takeLatest } from 'redux-saga/effects';

// Global actions
import { setResponse, setLoading } from '@nephro/redux/stores/app/actions';

// Services
import appointmentsService from '@nephro/services/appointments-zoom-service';

// Constants
import {
  SCHEDULE_APPOINTMENT_ZOOM,
  CANCEL_APPOINTMENT_ZOOM,
  RESCHEDULE_APPOINTMENT_ZOOM,
  GET_ZOOM_VIDEO_CALL_BY_ID,
  GET_MULTIPLE_ZOOM_VIDEO_CALL_BY_ID
} from './constants';

const customError = {
  type: 'Error',
  message: 'commons.problem'
};

/**
 * Call service to schedule patient appointment
 * @param { object } action - Action with payload data
 */
export function* scheduleAppointmentZoom(action) {
  yield put(setLoading(true));
  const {
    callback,
    payload: { data }
  } = action;

  const {
    capHorainicio,
    capId,
    profesional,
    tipoConsultaId,
    idPatient,
    idPatientAm
  } = data;

  try {
    const response = yield call(
      appointmentsService.scheduleAppointmentZoom,
      capHorainicio,
      capId,
      profesional,
      tipoConsultaId,
      idPatient,
      idPatientAm
    );
    yield put(setResponse('success', false, false, response, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        err.message || customError.message,
        false,
        false,
        callback
      )
    );
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to reschedule patient appointment
 * @param { object } action - Action with payload data
 */
export function* reScheduleAppointmentZoom(action) {
  yield put(setLoading(true));
  const {
    callback,
    payload: { data }
  } = action;

  const {
    capHorainicio,
    capId,
    profesional,
    tipoConsultaId,
    idPatientAm,
    quoteId,
    meetingId
  } = data;
  try {
    const response = yield call(
      appointmentsService.reScheduleAppointmentZoom,
      capHorainicio,
      capId,
      profesional,
      tipoConsultaId,
      idPatientAm,
      quoteId,
      meetingId
    );
    yield put(setResponse('success', false, false, response, callback));
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        err.message || customError.message,
        false,
        false,
        callback
      )
    );
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to cancel patient appointment
 * @param { object } action - Action with payload data
 */
export function* cancelAppointmentZoom(action) {
  yield put(setLoading(true));
  const {
    callback,
    payload: { data }
  } = action;

  const { quoteId, meetingId } = data;

  try {
    const response = yield call(
      appointmentsService.cancelAppointmentZoom,
      quoteId,
      meetingId
    );
    yield put(setResponse('success', false, false, response, callback));
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call process to get zoom video call by appointment Id
 * @param { object } action - Action with payload data
 */
export function* getZoomVideoCallById(action) {
  yield put(setLoading(true));
  const {
    callback,
    payload: { data }
  } = action;

  const { quoteId } = data;

  try {
    const response = yield call(
      appointmentsService.getZoomVideoCallById,
      quoteId
    );
    yield put(setResponse('success', false, false, response, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call process to get zoom video call by appointment Id
 * @param { object } action - Action with payload data
 */
export function* getMultipleZoomVideoCallById(action) {
  yield put(setLoading(true));
  const {
    callback,
    payload: { data }
  } = action;

  const { appointments } = data;
  try {
    const response = yield appointments.map((item) =>
      call(appointmentsService.getZoomVideoCallById, item.id)
    );
    yield put(setResponse('success', false, false, response, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Declare all component sagas
 */
export function* watchAppointmentsZoomSaga() {
  yield takeLatest(SCHEDULE_APPOINTMENT_ZOOM, scheduleAppointmentZoom);
  yield takeLatest(CANCEL_APPOINTMENT_ZOOM, cancelAppointmentZoom);
  yield takeLatest(RESCHEDULE_APPOINTMENT_ZOOM, reScheduleAppointmentZoom);
  yield takeLatest(GET_ZOOM_VIDEO_CALL_BY_ID, getZoomVideoCallById);
  yield takeLatest(
    GET_MULTIPLE_ZOOM_VIDEO_CALL_BY_ID,
    getMultipleZoomVideoCallById
  );
}
